/**
 * Modal para inscribir miembros de un club a un evento
 */

import { useState, useEffect, useMemo } from "react";
import { Button, RoleBadge, SectionBadge } from "@/components/ui";
import { toast } from "react-hot-toast";
import {
  X,
  Loader2,
  Search,
  UserPlus,
  Check,
  AlertCircle,
  Users,
  Trophy,
  FileCheck,
  Clock,
} from "lucide-react";
import { useCreateEventRegistration } from "@/hooks/useEvents";
import { usePermissions } from "@/hooks/usePermissions";
import { useMembersByClub } from "@/hooks/usePersons";
import {
  checkEventEligibility,
  describeException,
} from "@/lib/transitionalAge";
import { Timestamp } from "firebase/firestore";
import { useQuery } from "@tanstack/react-query";
import { churchService, clubService } from "@/lib/services/organization";
import { ACTIVE_REG_STATUSES } from "@/lib/services/events";
import {
  getActiveAmount,
  hasActiveEarlyBird,
  isClubDiscounted,
} from "@/lib/pricing";
import { CLUB_TYPE_LABELS, MEMBER_ROLE_LABELS } from "@/types";
import type { ClubType, Event, MemberLegacy as Member, MemberRole } from "@/types";

/** Chip compacto de la barra de filtros de la lista. */
function FiltroChip({
  activo,
  onClick,
  children,
}: {
  activo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors whitespace-nowrap ${
        activo
          ? "bg-purple-600 border-purple-600 text-white"
          : "bg-white border-gray-200 text-gray-600 hover:border-purple-300 hover:text-purple-700"
      }`}
    >
      {children}
    </button>
  );
}

// Helper para obtener el nombre del miembro independiente del formato (MemberWithPerson o MemberLegacy)
const getMemberName = (member: Member): string => {
  // MemberWithPerson: datos en member.person
  const p = (
    member as unknown as {
      person?: { fullName?: string; firstName?: string; lastName?: string };
    }
  ).person;
  if (p?.fullName) return p.fullName;
  if (p?.firstName || p?.lastName)
    return `${p.firstName || ""} ${p.lastName || ""}`.trim();
  // MemberLegacy: datos directos
  if (member.fullName) return member.fullName;
  if (member.firstName || member.lastName)
    return `${member.firstName || ""} ${member.lastName || ""}`.trim();
  // Fallback: personName desnormalizado en Member
  if (member.personName) return member.personName;
  return "Sin nombre";
};

interface EventRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Se llama cuando se inscribieron miembros correctamente
  event: Event;
  clubId: string;
  clubName: string;
  /**
   * ID de la iglesia del club (opcional). Se usa para resolver el distrito y
   * aplicar el descuento por distrito si el evento lo tiene configurado.
   */
  clubChurchId?: string;
  registeredPersonIds?: string[]; // IDs de personas ya inscritas
  currentClubRegisteredCount?: number; // Cuántos ya tiene inscritos el club
}

// Fallback para categorías estándar si el evento no define etiquetas
const defaultCategoryLabels: Record<string, string> = {
  pathfinder: "Conquistador/Aventurero",
  staff: "Staff/Consejero",
  guest: "Invitado",
  family: "Familiar",
};

// Helper para obtener etiqueta de categoría desde el evento
const getCategoryLabel = (event: Event, category: string): string => {
  const pricing = event.pricing.find((p) => p.category === category);
  if (pricing?.label) return pricing.label;
  return defaultCategoryLabels[category] || category;
};

export default function EventRegistrationModal({
  isOpen,
  onClose,
  onSuccess,
  event,
  clubId,
  clubName,
  clubChurchId,
  registeredPersonIds = [],
  currentClubRegisteredCount = 0,
}: EventRegistrationModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  /** Filtros de la lista larga (clubes de 130+ personas). */
  const [filtroSeccion, setFiltroSeccion] = useState<string>("todas");
  /** "todos" | "sin_cargo" | un MemberRole concreto (director, consejero…). */
  const [filtroCargo, setFiltroCargo] = useState<string>("todos");
  const [mobileTab, setMobileTab] = useState<"available" | "selected">(
    "available",
  );
  const [avisosAbiertos, setAvisosAbiertos] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<
    Map<string, { member: Member; category: string }>
  >(new Map());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState<{
    current: number;
    total: number;
    currentName: string;
    failed: number;
  } | null>(null);

  const { data: members, isLoading: loadingMembers } = useMembersByClub(clubId);

  // F13: para la excepción de edad de transición hace falta saber quiénes ya
  // fueron al campamento de su propia sección — esos no repiten etapa.
  // Cuenta solo la inscripción VIVA: quien canceló no fue, así que conserva su
  // oportunidad (2026-08-06: había 4 niños excluidos por una inscripción que
  // ellos mismos habían cancelado).
  const excludedEventId =
    event.transitionalAgeException?.enabled
      ? event.transitionalAgeException.excludeIfRegisteredInEventId
      : undefined;
  const [priorEventPersonIds, setPriorEventPersonIds] = useState<Set<string>>(
    new Set(),
  );
  useEffect(() => {
    let cancelled = false;
    if (!excludedEventId) {
      setPriorEventPersonIds(new Set());
      return;
    }
    (async () => {
      try {
        const { eventRegistrationService } = await import(
          "@/lib/services/events"
        );
        const regs = await eventRegistrationService.getByClubAndEvent(
          clubId,
          excludedEventId,
        );
        if (!cancelled) {
          setPriorEventPersonIds(
            new Set(
              regs
                .filter((r) => ACTIVE_REG_STATUSES.includes(r.status))
                .map((r) => r.personId)
                .filter(Boolean) as string[],
            ),
          );
        }
      } catch (e) {
        // Si no se puede leer, la excepción NO se concede a ciegas: se deja el
        // set vacío y el filtro decide con el resto de criterios.
        console.warn("[F13] no se pudieron leer inscripciones previas:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clubId, excludedEventId]);
  const createRegistration = useCreateEventRegistration();
  // Admins (asociación/unión) tienen control total: pueden inscribir aunque las
  // inscripciones ya estén cerradas por fecha (casos de último momento).
  const { isAdmin } = usePermissions();

  // Resolver el distrito del club a partir de la iglesia (si aplica).
  const eventHasDistrictDiscount =
    (event.discountedDistrictIds?.length ?? 0) > 0;
  const { data: clubChurch } = useQuery({
    queryKey: ["church", clubChurchId],
    queryFn: () =>
      clubChurchId
        ? churchService.getById(clubChurchId)
        : Promise.resolve(null),
    enabled: !!clubChurchId && eventHasDistrictDiscount,
    staleTime: 5 * 60 * 1000,
  });
  // En prod casi todas las iglesias traen districtId en null y el distrito
  // real viene en alfoliDistrictId, así que faltaba justamente la dependencia
  // que decide el descuento: sin ella el memo no se recalcula al llegar la
  // iglesia y el club paga tarifa plena.
  // La decisión explícita del club (hasSpecialRate) manda sobre el distrito:
  // hay clubes que comparten distrito y no comparten tarifa.
  const { data: clubDoc } = useQuery({
    queryKey: ["club-rate", clubId],
    queryFn: () => clubService.getById(clubId),
    enabled: !!clubId && eventHasDistrictDiscount,
    staleTime: 5 * 60 * 1000,
  });
  const isDiscounted = useMemo(
    () =>
      isClubDiscounted(
        clubChurch?.districtId ?? clubChurch?.alfoliDistrictId,
        event,
        clubDoc?.hasSpecialRate,
      ),
    [
      clubChurch?.districtId,
      clubChurch?.alfoliDistrictId,
      event,
      clubDoc?.hasSpecialRate,
    ],
  );

  // ¿Pudimos siquiera averiguar a qué distrito pertenece el club? Distinto de
  // "su distrito no tiene tarifa especial": aquí es que no hay dato.
  const distritoResuelto =
    !!clubChurchId &&
    !!(clubChurch?.districtId ?? clubChurch?.alfoliDistrictId);

  /**
   * Elegibles del club, ya descontando inscritos y seleccionados, ORDENADOS
   * alfabéticamente. Es la base sobre la que se cuentan los filtros.
   *
   * Hay clubes con más de 130 personas entre aventureros, conquistadores y
   * guías: sin orden ni filtros, el director tenía que recorrer una lista
   * larguísima en orden arbitrario para encontrar a los suyos.
   */
  const elegibles = useMemo(() => {
    if (!members) return [];
    return (members as Member[])
      .filter((member) => {
        if (member.personId && registeredPersonIds.includes(member.personId))
          return false;
        if (registeredPersonIds.includes(member.id)) return false;
        if (selectedMembers.has(member.id)) return false;
        // Secciones permitidas + excepción de edad de transición (F13). La
        // lógica es compartida para que UI y validaciones no diverjan.
        return checkEventEligibility(member, event, priorEventPersonIds)
          .eligible;
      })
      .sort((a, b) =>
        getMemberName(a).localeCompare(getMemberName(b), "es", {
          sensitivity: "base",
        }),
      );
  }, [members, registeredPersonIds, selectedMembers, event, priorEventPersonIds]);

  /** Sección efectiva para agrupar: la de staff manda sobre la etaria. */
  const seccionDe = (m: Member): string =>
    ((m as unknown as { staffSection?: string; section?: string })
      .staffSection ??
      (m as unknown as { section?: string }).section ??
      "") as string;

  const esStaff = (m: Member): boolean => {
    const rol = (m as unknown as { role?: string }).role ?? "member";
    return rol !== "member";
  };

  // Secciones presentes entre los elegibles, con su conteo.
  const seccionesDisponibles = useMemo(() => {
    const cuenta = new Map<string, number>();
    elegibles.forEach((m) => {
      const s = seccionDe(m);
      if (s) cuenta.set(s, (cuenta.get(s) ?? 0) + 1);
    });
    return [...cuenta.entries()].sort((a, b) => b[1] - a[1]);
  }, [elegibles]);

  /** Cargos presentes entre los elegibles, con su conteo. Mismos cargos y
   *  etiquetas que la pantalla de Miembros (MEMBER_ROLE_LABELS). */
  const cargosDisponibles = useMemo(() => {
    const cuenta = new Map<MemberRole, number>();
    elegibles.forEach((m) => {
      const rol = ((m as unknown as { role?: MemberRole }).role ??
        "member") as MemberRole;
      if (rol === "member") return;
      cuenta.set(rol, (cuenta.get(rol) ?? 0) + 1);
    });
    return [...cuenta.entries()].sort((a, b) => b[1] - a[1]);
  }, [elegibles]);

  const conteoStaff = useMemo(
    () => elegibles.filter(esStaff).length,
    [elegibles],
  );

  // Lista final que se pinta: elegibles + filtros de la barra.
  const availableMembers = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    return elegibles.filter((member) => {
      if (filtroSeccion === "__transicion") {
        // Los que entran por la excepción de edad (F13).
        if (!checkEventEligibility(member, event, priorEventPersonIds).byException)
          return false;
      } else if (filtroSeccion !== "todas" && seccionDe(member) !== filtroSeccion)
        return false;
      if (filtroCargo !== "todos") {
        const rol = ((member as unknown as { role?: MemberRole }).role ??
          "member") as MemberRole;
        if (filtroCargo === "directiva") {
          if (rol === "member") return false;
        } else if (filtroCargo === "sin_cargo") {
          if (rol !== "member") return false;
        } else if (rol !== filtroCargo) return false;
      }
      if (search) {
        // Mismos campos que el buscador de Gestión de Miembros.
        const per = (member as unknown as {
          person?: { email?: string; idDocumentNumber?: string; phone?: string };
          email?: string;
          idDocumentNumber?: string;
          phone?: string;
        });
        const campos = [
          getMemberName(member),
          per.person?.email ?? per.email ?? "",
          per.person?.idDocumentNumber ?? per.idDocumentNumber ?? "",
          per.person?.phone ?? per.phone ?? "",
        ];
        if (!campos.some((c) => String(c).toLowerCase().includes(search)))
          return false;
      }
      return true;
    });
  }, [elegibles, searchTerm, filtroSeccion, filtroCargo, event, priorEventPersonIds]);

  // Calcular totales
  const totals = useMemo(() => {
    let total = 0;
    let count = 0;

    selectedMembers.forEach(({ category }) => {
      const pricing = event.pricing.find((p) => p.category === category);
      if (pricing) {
        total += getActiveAmount(pricing, isDiscounted);
        count++;
      }
    });

    return { total, count };
  }, [selectedMembers, event.pricing, isDiscounted]);

  const handleSelectMember = (member: Member) => {
    setSelectedMembers((prev) => {
      const newMap = new Map(prev);
      // Sin categoría por defecto: el director debe elegirla manualmente.
      newMap.set(member.id, { member, category: "" });
      return newMap;
    });
  };

  const handleRemoveMember = (memberId: string) => {
    setSelectedMembers((prev) => {
      const newMap = new Map(prev);
      newMap.delete(memberId);
      return newMap;
    });
  };

  const handleCategoryChange = (memberId: string, category: string) => {
    setSelectedMembers((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(memberId);
      if (existing) {
        newMap.set(memberId, { ...existing, category });
      }
      return newMap;
    });
  };

  const handleSubmit = async () => {
    if (selectedMembers.size === 0) return;

    const total = selectedMembers.size;
    setIsSubmitting(true);
    setProgress({ current: 0, total, currentName: "", failed: 0 });
    const succeeded: string[] = [];
    const failed: string[] = [];

    let index = 0;
    for (const [_, { member, category }] of selectedMembers.entries()) {
      index += 1;
      const memberName = getMemberName(member);
      setProgress({
        current: index,
        total,
        currentName: memberName,
        failed: failed.length,
      });
      const pricing = event.pricing.find((p) => p.category === category);

      if (!pricing) {
        failed.push(`${memberName} (sin precio para categoría)`);
        continue;
      }
      if (!member.personId) {
        failed.push(`${memberName} (sin registro de persona)`);
        continue;
      }

      try {
        await createRegistration.mutateAsync({
          eventId: event.id,
          eventName: event.name,
          clubId,
          clubName,
          registeredById: "",
          registeredByName: "",
          personId: member.personId,
          personName: memberName,
          memberId: member.id,
          category: category as "pathfinder" | "staff" | "guest" | "family",
          unitPrice: getActiveAmount(pricing, isDiscounted),
          status: event.registrationConfig.requiresApproval
            ? "pending"
            : "approved",
          paymentStatus: "pending",
          amountPaid: 0,
          registrationDate: Timestamp.now(),
          // Admin: omite el cierre por fecha para inscripción de último momento.
          skipDateChecks: isAdmin,
        });
        succeeded.push(memberName);
      } catch (error) {
        // Un "Missing or insufficient permissions" crudo, repetido una vez por
        // miembro, hace pensar que la app se cayó. La causa habitual es que el
        // reloj del equipo va adelantado y el servidor todavía no abrió las
        // inscripciones (la ventana la valida el servidor desde 2026-08-06).
        const code = (error as { code?: string })?.code;
        const msg =
          code === "permission-denied"
            ? "el servidor aún no abrió las inscripciones (revisa la hora de tu equipo)"
            : error instanceof Error
              ? error.message
              : "Error desconocido";
        failed.push(`${memberName} (${msg})`);
      }
    }

    if (succeeded.length > 0 && failed.length === 0) {
      toast.success(`${succeeded.length} miembro(s) inscrito(s) exitosamente.`);
      setIsSubmitting(false);
      setProgress(null);
      onClose();
      onSuccess?.();
      return;
    } else if (succeeded.length > 0 && failed.length > 0) {
      toast.error(
        `${succeeded.length} inscrito(s), ${failed.length} fallido(s): ${failed.join(", ")}`,
        { duration: 6000 },
      );
    } else {
      toast.error(`No se pudo inscribir: ${failed.join(", ")}`, {
        duration: 6000,
      });
    }
    setIsSubmitting(false);
    setProgress(null);
  };

  // Limpiar selección al cerrar
  useEffect(() => {
    if (!isOpen) {
      setSelectedMembers(new Map());
      setSearchTerm("");
    }
  }, [isOpen]);

  // Avisar al usuario si intenta cerrar la pestaña/ventana mientras se envía
  useEffect(() => {
    if (!isSubmitting) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isSubmitting]);

  if (!isOpen) return null;

  const getPriceForCategory = (category: string): number => {
    const pricing = event.pricing.find((p) => p.category === category);
    return pricing ? getActiveAmount(pricing, isDiscounted) : 0;
  };

  const earlyBird = hasActiveEarlyBird(event);

  /** Una línea con lo que hay que saber; el detalle queda plegado. */
  const resumenAvisos = [
    isDiscounted
      ? `Precio con descuento${event.discountLabel ? ` (${event.discountLabel})` : ""}`
      : eventHasDistrictDiscount && !distritoResuelto
        ? "Tarifa plena: tu club no tiene distrito registrado"
        : null,
    currentClubRegisteredCount >= 10
      ? `Tu club ya está en el ranking (${currentClubRegisteredCount})`
      : `Faltan ${Math.max(0, 10 - currentClubRegisteredCount - selectedMembers.size)} para el ranking`,
    (event.clubTypesAllowed?.length ?? 0) > 0
      ? `Solo ${event.clubTypesAllowed!.map((t) => CLUB_TYPE_LABELS[t as ClubType] ?? t).join(", ")}`
      : null,
    event.transitionalAgeException?.enabled ? "Incluye edad de transición" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const handleBackdropClick = () => {
    if (isSubmitting) return; // bloquear cierre durante envío
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Inscribir miembros al evento"
      onClick={handleBackdropClick}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[96vh] sm:max-h-[93vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200 relative flex flex-col"
      >
        {/* Overlay de progreso durante envío masivo */}
        {isSubmitting && progress && (
          <div className="absolute inset-0 z-10 bg-white/95 backdrop-blur-sm flex items-center justify-center rounded-2xl">
            <div className="w-full max-w-md px-8 py-10 text-center">
              <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                <Loader2 className="w-8 h-8 text-white animate-spin" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Inscribiendo miembros
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                {progress.current} de {progress.total} procesados
                {progress.failed > 0 && (
                  <span className="text-red-600">
                    {" "}
                    · {progress.failed} con error
                  </span>
                )}
              </p>

              <div
                className="w-full h-3 bg-gray-100 rounded-full overflow-hidden mb-3"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={progress.total}
                aria-valuenow={progress.current}
              >
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 transition-all duration-300"
                  style={{
                    width: `${Math.round((progress.current / progress.total) * 100)}%`,
                  }}
                />
              </div>

              <p
                className="text-xs text-gray-500 truncate min-h-[1rem]"
                title={progress.currentName}
              >
                {progress.currentName
                  ? `Procesando: ${progress.currentName}`
                  : ""}
              </p>

              <p className="mt-6 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                <strong>No cierres esta ventana</strong> hasta que termine. Las
                inscripciones se guardan una por una.
              </p>
            </div>
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-purple-100 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-t-2xl shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 bg-white/20 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-xl font-semibold text-white truncate">
                Inscribir Miembros
              </h2>
              <p className="text-xs sm:text-sm text-purple-100 mt-0.5 sm:mt-1 truncate">
                {event.name} • {clubName}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 hover:bg-white/20 rounded-xl transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Los avisos van plegados: apilados ocupaban casi la mitad del alto
            del modal y dejaban la lista de miembros en una rendija. El
            resumen de una línea basta para saber si hay algo que leer. */}
        <button
          type="button"
          onClick={() => setAvisosAbiertos((v) => !v)}
          className="w-full flex items-center gap-2 px-4 sm:px-6 py-2 bg-gray-50 border-b border-gray-200 text-left shrink-0 hover:bg-gray-100 transition-colors"
        >
          <AlertCircle className="w-4 h-4 text-gray-400 shrink-0" />
          <span className="text-xs text-gray-600 flex-1 min-w-0 truncate">
            {resumenAvisos}
          </span>
          <span className="text-xs font-semibold text-purple-700 shrink-0">
            {avisosAbiertos ? "Ocultar" : "Ver detalles"}
          </span>
        </button>

        {avisosAbiertos && (
        // Tope propio con scroll: en portátiles de 900px los cinco avisos
        // desplegados se comían toda la altura del modal y la lista de
        // miembros quedaba en cero — el director veía el buscador y
        // "Seleccionados (16)" sobre un vacío, sin a quién marcar.
        <div className="shrink-0 max-h-[30vh] overflow-y-auto">
        {/* Banner descuento por distrito */}
        {isDiscounted && (
          <div className="px-4 sm:px-6 py-2 sm:py-2.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2 text-xs sm:text-sm text-amber-800 shrink-0">
            <Trophy className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <span>
              Aplica precio con descuento
              {event.discountLabel ? (
                <strong> ({event.discountLabel})</strong>
              ) : null}{" "}
              para tu club.
            </span>
          </div>
        )}

        {/* El evento tiene tarifa especial por distrito pero no sabemos a cuál
            pertenece este club: se le cobraría tarifa plena sin decirle nada.
            En prod hay 7 clubes así (sin iglesia asignada, o con la iglesia sin
            distrito), y la diferencia son $10.000 por inscrito. */}
        {eventHasDistrictDiscount && !isDiscounted && !distritoResuelto && (
          <div className="px-4 sm:px-6 py-2 sm:py-2.5 bg-blue-50 border-b border-blue-200 flex items-start gap-2 text-xs sm:text-sm text-blue-800 shrink-0">
            <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <span>
              Tu club no tiene iglesia o distrito registrado, así que se está
              aplicando la <strong>tarifa plena</strong>. Si tu distrito tiene
              tarifa especial, avísale a la Asociación <em>antes</em> de
              inscribir: el precio queda congelado en cada inscripción.
            </span>
          </div>
        )}

        {/* Info ranking */}
        <div className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 shrink-0">
          <div className="flex items-start gap-2 sm:gap-3">
            <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm text-amber-800 leading-snug">
                <strong>Ranking:</strong>{" "}
                {currentClubRegisteredCount >= 10 ? (
                  <>
                    <FileCheck className="w-3 h-3 inline" /> ¡Tu club ya está en
                    el ranking con <strong>{currentClubRegisteredCount}</strong>{" "}
                    inscritos! Sigue inscribiendo miembros hasta la fecha
                    límite.
                    {selectedMembers.size > 0 && (
                      <span className="text-amber-600 ml-1">
                        (+{selectedMembers.size} seleccionados)
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    Tu club necesita un mínimo de{" "}
                    <strong>10 miembros con recibo</strong> subido para entrar
                    al ranking. Después puedes seguir inscribiendo sin límite.
                    {currentClubRegisteredCount > 0 && (
                      <span className="ml-1">
                        Llevas <strong>{currentClubRegisteredCount}</strong>{" "}
                        inscrito{currentClubRegisteredCount !== 1 ? "s" : ""}
                        {selectedMembers.size > 0 && (
                          <span className="text-amber-600">
                            {" "}
                            (+{selectedMembers.size} seleccionados ={" "}
                            {currentClubRegisteredCount + selectedMembers.size})
                          </span>
                        )}
                        .
                      </span>
                    )}
                    {currentClubRegisteredCount + selectedMembers.size < 10 && (
                      <span className="ml-1 font-medium">
                        Faltan{" "}
                        {10 - currentClubRegisteredCount - selectedMembers.size}{" "}
                        más para el ranking.
                      </span>
                    )}
                    {currentClubRegisteredCount + selectedMembers.size >=
                      10 && (
                      <span className="ml-1">
                        <FileCheck className="w-3 h-3 inline" /> ¡Alcanzas el
                        mínimo!
                      </span>
                    )}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

          {event.clubTypesAllowed && event.clubTypesAllowed.length > 0 && (
            <div className="px-4 sm:px-6 py-2 bg-amber-50 border-b border-amber-200 flex items-start gap-2 text-xs text-amber-800">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Este evento solo acepta miembros de:{" "}
                <b>
                  {event.clubTypesAllowed
                    .map((t) => CLUB_TYPE_LABELS[t as ClubType] ?? t)
                    .join(", ")}
                </b>
                . Los demás están ocultos.
              </span>
            </div>
          )}
          {/* F13: por qué aparecen nombres de otra sección. */}
          {event.transitionalAgeException?.enabled && (
            <div className="px-4 sm:px-6 py-2 bg-emerald-50 border-b border-emerald-200 flex items-start gap-2 text-xs text-emerald-900">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                {describeException(event)} Aparecen marcados como{" "}
                <b>
                  {event.transitionalAgeException.label ?? "Edad de transición"}
                </b>
                .
              </span>
            </div>
          )}
        </div>
        )}

        {/* Tabs mobile */}
        <div className="md:hidden flex border-b border-gray-200 bg-gray-50 shrink-0">
          <button
            onClick={() => setMobileTab("available")}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              mobileTab === "available"
                ? "text-purple-700 border-b-2 border-purple-600 bg-white"
                : "text-gray-600"
            }`}
          >
            Disponibles
          </button>
          <button
            onClick={() => setMobileTab("selected")}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              mobileTab === "selected"
                ? "text-purple-700 border-b-2 border-purple-600 bg-white"
                : "text-gray-600"
            }`}
          >
            Seleccionados
            {selectedMembers.size > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full bg-purple-600 text-white">
                {selectedMembers.size}
              </span>
            )}
          </button>
        </div>

        {/* min-h: la lista nunca cede por completo ante los avisos, pase lo
            que pase con la altura de la pantalla. */}
        <div className="flex flex-col md:flex-row flex-1 min-h-[260px]">
          {/* Panel izquierdo: Selección de miembros */}
          <div
            className={`md:w-3/5 md:border-r border-gray-100 flex-col min-h-0 ${
              mobileTab === "available" ? "flex" : "hidden md:flex"
            }`}
          >
            <div className="px-3 pt-2.5 pb-2 border-b border-gray-100">
              {/* Estos dos avisos explican por qué la lista trae a unos y no a
                  otros. Iban expandidos y empujaban la lista fuera de la
                  pantalla; ahora viven en el bloque plegable de arriba. */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por nombre, email o documento..."
                  className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300"
                />
              </div>

              {/* Chips compactos, pero con los MISMOS valores con los que
                  filtra Gestión de Miembros: cada cargo real (Director,
                  Consejero, Tesorero…) y cada sección, con su conteo. No un
                  binario staff/no-staff inventado aquí. */}
              {(seccionesDisponibles.length > 1 || conteoStaff > 0) && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1">
                  <FiltroChip
                    activo={
                      filtroSeccion === "todas" && filtroCargo === "todos"
                    }
                    onClick={() => {
                      setFiltroSeccion("todas");
                      setFiltroCargo("todos");
                    }}
                  >
                    Todos ({elegibles.length})
                  </FiltroChip>

                  {seccionesDisponibles.map(([sec, n]) => (
                    <FiltroChip
                      key={sec}
                      activo={filtroSeccion === sec}
                      onClick={() =>
                        setFiltroSeccion(filtroSeccion === sec ? "todas" : sec)
                      }
                    >
                      {CLUB_TYPE_LABELS[sec as ClubType] ?? sec} ({n})
                    </FiltroChip>
                  ))}

                  {event.transitionalAgeException?.enabled && (
                    <FiltroChip
                      activo={filtroSeccion === "__transicion"}
                      onClick={() =>
                        setFiltroSeccion(
                          filtroSeccion === "__transicion" ? "todas" : "__transicion",
                        )
                      }
                    >
                      Edad de transición
                    </FiltroChip>
                  )}

                  {conteoStaff > 0 && (
                    <>
                      <span className="mx-0.5 h-4 w-px bg-gray-200" />
                      <FiltroChip
                        activo={filtroCargo === "directiva"}
                        onClick={() =>
                          setFiltroCargo(
                            filtroCargo === "directiva" ? "todos" : "directiva",
                          )
                        }
                      >
                        Directiva ({conteoStaff})
                      </FiltroChip>
                      {/* Los cargos concretos se despliegan al entrar en
                          "Directiva": en el celular, mostrarlos siempre eran
                          cuatro filas de chips y la lista se quedaba en seis
                          nombres. */}
                      {(filtroCargo === "directiva" ||
                        cargosDisponibles.some(([r]) => r === filtroCargo)) &&
                        cargosDisponibles.map(([rol, n]) => (
                          <FiltroChip
                            key={rol}
                            activo={filtroCargo === rol}
                            onClick={() =>
                              setFiltroCargo(
                                filtroCargo === rol ? "directiva" : rol,
                              )
                            }
                          >
                            {MEMBER_ROLE_LABELS[rol]} ({n})
                          </FiltroChip>
                        ))}
                      <FiltroChip
                        activo={filtroCargo === "sin_cargo"}
                        onClick={() =>
                          setFiltroCargo(
                            filtroCargo === "sin_cargo" ? "todos" : "sin_cargo",
                          )
                        }
                      >
                        Sin cargo ({elegibles.length - conteoStaff})
                      </FiltroChip>
                    </>
                  )}
                </div>
              )}

              {/* Cuántos se ven de cuántos + añadir toda la lista de un golpe.
                  Con 40 aventureros, esto ahorra 40 clics. */}
              {elegibles.length > 0 && (
                <div className="mt-1.5 flex items-center justify-between gap-2 text-xs">
                  <span className="text-gray-500">
                    Mostrando <b className="text-gray-800">{availableMembers.length}</b>{" "}
                    de {elegibles.length}
                  </span>
                  {availableMembers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => availableMembers.forEach(handleSelectMember)}
                      className="font-semibold text-purple-700 hover:text-purple-900 underline underline-offset-2"
                    >
                      Agregar los {availableMembers.length} que se ven
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {loadingMembers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                </div>
              ) : availableMembers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <UserPlus className="w-8 h-8 text-gray-500" />
                  </div>
                  <p className="font-medium">No hay miembros disponibles</p>
                  <p className="text-sm mt-1">
                    {searchTerm
                      ? "No se encontraron coincidencias"
                      : "Todos los miembros ya están inscritos o seleccionados"}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-1">
                  {availableMembers.map((member: Member) => {
                    const memberName = getMemberName(member);
                    return (
                      <button
                        key={member.id}
                        onClick={() => handleSelectMember(member)}
                        className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg border border-gray-100 hover:border-purple-300 hover:bg-purple-50 transition-all duration-200 text-left"
                      >
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 rounded-lg flex items-center justify-center shrink-0">
                          <span className="text-white font-semibold text-sm">
                            {memberName
                              .split(" ")
                              .map((n: string) => n[0])
                              .join("")
                              .slice(0, 2)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate leading-tight">
                            {memberName}
                          </p>
                          {/* Mismos chips que la pantalla de Miembros
                              (RoleBadge / SectionBadge), incluido el matiz de
                              "por edad" cuando la sección donde sirve no es la
                              que le toca por edad. */}
                          <div className="flex items-center gap-1 flex-wrap leading-tight">
                            {(() => {
                              const rol = ((member as unknown as { role?: MemberRole })
                                .role ?? "member") as MemberRole;
                              const sec = seccionDe(member) as ClubType;
                              const etaria = (member as unknown as { section?: ClubType })
                                .section;
                              const cruzada = !!etaria && !!sec && etaria !== sec;
                              return (
                                <>
                                  {rol !== "member" ? (
                                    <RoleBadge
                                      role={rol}
                                      section={sec || undefined}
                                      showSection={!!sec}
                                      size="xs"
                                    />
                                  ) : sec ? (
                                    <SectionBadge section={sec} size="xs" variant="soft" />
                                  ) : null}
                                  {cruzada && (
                                    <span className="text-[10px] text-gray-400">
                                      {CLUB_TYPE_LABELS[etaria] ?? etaria} por edad
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                            {(() => {
                              // F13: por qué aparece alguien de otra sección.
                              const el = checkEventEligibility(
                                member,
                                event,
                                priorEventPersonIds,
                              );
                              return el.byException ? (
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                                  {el.label}
                                </span>
                              ) : null;
                            })()}
                          </div>
                        </div>
                        <UserPlus className="w-4 h-4 text-purple-500" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Panel derecho: Miembros seleccionados */}
          <div
            className={`md:w-2/5 flex-col min-h-0 ${
              mobileTab === "selected" ? "flex" : "hidden md:flex"
            }`}
          >
            <div className="p-4 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">
                  Seleccionados ({selectedMembers.size})
                </h3>
                {selectedMembers.size > 0 && (
                  <button
                    onClick={() => setSelectedMembers(new Map())}
                    className="text-sm text-red-600 hover:text-red-700 transition-colors duration-200"
                  >
                    Limpiar todo
                  </button>
                )}
              </div>

              {/* Categoría para TODOS de una vez. Sin esto, agregar 63
                  personas obliga a abrir 63 desplegables uno por uno, que es
                  el cuello de botella real de un club grande. */}
              {selectedMembers.size > 1 && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-gray-500 shrink-0">
                    Tipo de entrada para los {selectedMembers.size}:
                  </span>
                  <select
                    value=""
                    onChange={(e) => {
                      const cat = e.target.value;
                      if (!cat) return;
                      setSelectedMembers((prev) => {
                        const next = new Map(prev);
                        next.forEach((v, k) => next.set(k, { ...v, category: cat }));
                        return next;
                      });
                    }}
                    className="flex-1 min-w-0 px-2 py-1.5 text-xs border-2 border-purple-200 rounded-lg bg-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                  >
                    <option value="">Aplicar a todos…</option>
                    {event.pricing.map((pr) => (
                      <option key={pr.category} value={pr.category}>
                        {getCategoryLabel(event, pr.category)} —{" "}
                        {getPriceForCategory(pr.category).toLocaleString("es-CO")}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {selectedMembers.size === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Check className="w-8 h-8 text-gray-500" />
                  </div>
                  <p className="font-medium">Ningún miembro seleccionado</p>
                  <p className="text-sm mt-1">
                    Selecciona miembros de la lista izquierda
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {Array.from(selectedMembers.entries()).map(
                    ([memberId, { member, category }]) => (
                      <div
                        key={memberId}
                        className="px-2.5 py-2 rounded-lg border border-green-200 bg-green-50/70"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-6 h-6 bg-green-500 rounded-md flex items-center justify-center shrink-0">
                              <Check className="w-3.5 h-3.5 text-white" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate leading-tight">
                                {getMemberName(member)}
                              </p>
                              <div className="leading-tight">
                                {seccionDe(member) ? (
                                  <SectionBadge
                                    section={seccionDe(member) as ClubType}
                                    size="xs"
                                    variant="soft"
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveMember(memberId)}
                            className="p-1 hover:bg-red-100 rounded-lg text-red-500 transition-colors shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="mt-1.5 flex items-center gap-2">
                          <select
                            value={category}
                            onChange={(e) =>
                              handleCategoryChange(memberId, e.target.value)
                            }
                            className={`flex-1 min-w-0 px-2 py-1 text-xs border rounded-lg focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-colors ${
                              category
                                ? "border-gray-200"
                                : "border-red-300 bg-red-50"
                            }`}
                          >
                            <option value="" disabled>
                              Seleccione tipo de entrada…
                            </option>
                            {event.pricing.map((pricing) => (
                              <option
                                key={pricing.category}
                                value={pricing.category}
                              >
                                {pricing.label ||
                                  getCategoryLabel(event, pricing.category)}
                              </option>
                            ))}
                          </select>
                          <span className="text-sm font-semibold text-gray-700">
                            $
                            {getPriceForCategory(category).toLocaleString(
                              "es-CO",
                            )}
                            {earlyBird && (
                              <span className="ml-1 text-xs text-emerald-600 font-normal">
                                (pronto pago)
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>

            {/* Resumen de totales */}
            {selectedMembers.size > 0 && (
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-600">Inscripciones:</span>
                  <span className="font-medium">{totals.count}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-900 font-medium">
                    Total a pagar:
                  </span>
                  <span className="text-xl font-bold text-purple-600">
                    ${totals.total.toLocaleString("es-CO")}
                  </span>
                </div>

                {earlyBird && (
                  <div className="mt-3 flex items-start gap-2 p-2 bg-emerald-50 rounded-xl">
                    <Clock className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-emerald-700">
                      <strong>Precio de pronto pago activo.</strong> Inscríbete
                      antes de la fecha límite para aprovechar el descuento.
                    </p>
                  </div>
                )}

                {event.registrationConfig.requiresApproval && (
                  <div className="mt-3 flex items-start gap-2 p-2 bg-yellow-50 rounded-xl">
                    <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-yellow-700">
                      Las inscripciones requieren aprobación del organizador
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 sm:gap-3 p-3 sm:p-6 border-t border-gray-200 bg-gray-50 shrink-0">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 sm:flex-none"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              selectedMembers.size === 0 ||
              isSubmitting ||
              Array.from(selectedMembers.values()).some((s) => !s.category)
            }
            className="flex-1 sm:flex-none"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Inscribiendo...
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4 mr-2" />
                Inscribir{" "}
                {selectedMembers.size > 0 ? `(${selectedMembers.size})` : ""}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
